/**
 * MiniQuizModal – 3-card post-session recap shown at the end of a Teacher
 * Mode session. Reuses the /api/teacher/quiz endpoint.
 *
 * Each card shows target + native + respelling. The learner can tap "Hear
 * it" to replay the target via TTS, then tap "Got it" to advance. No voice
 * scoring here — this is a lightweight confidence check, not a gate.
 *
 * Props:
 *   - open, sessionId, onClose
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, SpeakerHigh, SpinnerGap, CaretRight, FlagCheckered, GraduationCap,
} from '@phosphor-icons/react';
import { http, API, apiErrorMessage, getStoredToken } from '@/lib/api';

export default function MiniQuizModal({ open, sessionId, onClose }) {
  const [cards, setCards] = useState(null);
  const [loading, setLoading] = useState(false);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !sessionId) return;
    setCards(null); setIdx(0); setError('');
    (async () => {
      setLoading(true);
      try {
        const { data } = await http.post('/teacher/quiz', { session_id: sessionId });
        setCards(data.phrases || []);
      } catch (e) { setError(apiErrorMessage(e)); }
      finally { setLoading(false); }
    })();
  }, [open, sessionId]);

  async function speak(text) {
    if (!text) return;
    setPlaying(true);
    try {
      const resp = await fetch(`${API}/tts`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(getStoredToken() ? { Authorization: `Bearer ${getStoredToken()}` } : {}),
        },
        body: JSON.stringify({ text, speed: 0.9 }),
      });
      if (!resp.ok) throw new Error();
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => { URL.revokeObjectURL(url); setPlaying(false); };
      audio.onerror = () => { URL.revokeObjectURL(url); setPlaying(false); };
      audio.play().catch(() => setPlaying(false));
    } catch { setPlaying(false); }
  }

  const card = cards?.[idx];
  const total = cards?.length || 0;
  const atEnd = cards && idx >= total - 1;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/50"
            data-testid="mini-quiz-backdrop"
          />
          <motion.div
            key="modal"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed inset-x-4 top-1/2 z-50 -translate-y-1/2 rounded-3xl bg-[var(--lf-surface)] p-6 shadow-2xl"
            data-testid="mini-quiz-modal"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <GraduationCap size={18} weight="duotone" className="text-[var(--lf-primary)]" />
                <div className="font-bold">Quick recap</div>
              </div>
              <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full bg-[var(--lf-surface-muted)]" data-testid="mini-quiz-close" aria-label="Close quiz">
                <X size={14} aria-hidden="true" />
              </button>
            </div>

            {loading ? (
              <div className="mt-6 text-center text-sm text-[var(--lf-text-muted)]">
                <SpinnerGap size={16} className="inline animate-spin" /> Building your recap…
              </div>
            ) : error ? (
              <div className="mt-4 text-sm text-[var(--lf-destructive)]">{error}</div>
            ) : cards?.length ? (
              <>
                <div className="mt-4 text-xs text-[var(--lf-text-muted)] text-center" data-testid="mini-quiz-progress">
                  Card {idx + 1} of {total}
                </div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    className="mt-3 rounded-2xl border border-[var(--lf-border)] bg-[var(--lf-surface-muted)] p-5 text-center"
                    data-testid={`mini-quiz-card-${idx}`}
                  >
                    <div className="text-2xl font-extrabold">{card.target}</div>
                    {card.respelling ? (
                      <div className="mt-1 text-xs text-[var(--lf-text-muted)] font-mono">
                        {card.respelling}{card.ipa ? ` · /${card.ipa}/` : ''}
                      </div>
                    ) : null}
                    <div className="mt-2 text-sm">{card.native}</div>
                    {card.hint ? <div className="mt-2 text-xs italic text-[var(--lf-text-muted)]">{card.hint}</div> : null}
                    <button
                      onClick={() => speak(card.target)}
                      disabled={playing}
                      className="lf-btn lf-btn-secondary mt-4 w-full py-2 text-sm"
                      data-testid="mini-quiz-hear-btn"
                    >
                      {playing ? <SpinnerGap size={14} className="animate-spin" /> : <SpeakerHigh size={14} weight="fill" />}
                      Hear it
                    </button>
                  </motion.div>
                </AnimatePresence>
                <div className="mt-4 flex gap-2">
                  {atEnd ? (
                    <button onClick={onClose} className="lf-btn lf-btn-primary w-full" data-testid="mini-quiz-finish-btn">
                      <FlagCheckered size={14} weight="fill" /> Finish
                    </button>
                  ) : (
                    <button
                      onClick={() => setIdx((i) => Math.min(total - 1, i + 1))}
                      className="lf-btn lf-btn-primary w-full"
                      data-testid="mini-quiz-next-btn"
                    >
                      Got it <CaretRight size={14} weight="bold" />
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="mt-6 text-center text-sm text-[var(--lf-text-muted)]">
                No quiz items — nice chat!
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
